<script>
import AkLabel from '@components/v2/general/AkLabel.vue';
import AkSelect from '@components/v2/input/AkSelect.vue';
import utilsMixin from '@mixins/utilsMixin';
import Chart from 'primevue/chart';
import AkShimmer from '@components/v2/general/AkShimmer.vue';
import IndicatorCardGraphDialog from '@views/revenuemanagement/parts/IndicatorCardGraphDialog.vue';
import moment from 'moment';
import formatMixin from '@mixins/formatMixin';
import {Vue3Lottie} from 'vue3-lottie';

export default {
  name: 'IndicatorCard',
  components: {IndicatorCardGraphDialog, AkShimmer, AkSelect, AkLabel, Chart, Vue3Lottie},
  mixins: [utilsMixin, formatMixin],
  inject: {
    dashboard: {default: null},
    displayMode: {default: 'card'},
    interval: {default: 'weekly'},
    periodLabels: {default: {main: 'Période principale', comparison: 'Période comparée'}},
  },
  props: {
    technicalKey: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    information: {
      type: String,
      required: false,
    },
    icon: {
      type: String,
      required: false,
      default: null,
    },
    iconColor: {
      type: String,
      required: false,
    },
    iconBgColor: {
      type: String,
      required: false,
    },
    unit: {
      type: String,
      required: false,
    },
    positiveDeltaIsBad: {
      type: Boolean,
      default: false,
    },
    graphType: {
      type: String,
      default: 'line',
    },
    hideZeroLabels: {
      type: Boolean,
      required: false,
      default: false,
    },
    currencyIndicator: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['intervalChange'],
  data() {
    return {
      displayOptions: [
        {icon: 'ga-icon ga-t4', value: 'card'},
        {icon: 'ga-icon ga-stats', value: 'graph'},
      ],
      displayModeValue: 'card',
    };
  },
  computed: {
    hasIcon() {
      return this.icon !== null;
    },
    hasPicto() {
      return this.$slots.picto;
    },
    isGraph() {
      return this.displayModeValue === 'graph';
    },
    isLoading() {
      return (
        (this.dashboard?.indicatorsLoading[this.technicalKey] ?? false) || this.dashboard?.isPreparing.value === true
      );
    },
    indicator() {
      const bareObj = this.dashboard?.indicators[this.technicalKey];
      const res = this.isGraph ? bareObj?.indicatorGraphDTO : bareObj?.indicatorValueDTO;
      return {...res, uuid: bareObj?.uuid, key: bareObj?.key};
    },
    hasError() {
      return !!this.dashboard?.indicatorsError[this.technicalKey] || (!this.isLoading && !this.indicator);
    },
    delta() {
      if (!this.indicator?.main || !this.indicator?.comparison) {
        return null;
      }
      return (
        ((this.indicator.main - this.indicator.comparison) / this.indicator.comparison) *
        100 *
        (this.positiveDeltaIsBad ? -1 : 1)
      ).toFixed(1);
    },
    mainMap() {
      const main = this.indicator?.main ?? {};
      const keys = Object.keys(main).map((k) => this.displayLabel(k));
      const values = Object.values(main);
      return {
        keys,
        values,
      };
    },
    comparisonMap() {
      const main = Object.keys(this.indicator?.main ?? {});
      const comp = Object.keys(this.indicator?.comparison ?? {});
      while (comp.length < main.length) {
        comp.push(null);
      }
      const keys = [];
      const values = [];
      for (let i = 0; i < main.length; i++) {
        keys.push(this.displayLabel(comp[i]));
        if (comp[i]) values.push((this.indicator?.comparison ?? {})[comp[i]]);
      }
      return {
        keys,
        values,
      };
    },
    chartData() {
      const data = {
        labels: this.mainMap.keys,
        datasets: [
          {
            label: this.periodLabels.main,
            data: this.mainMap.values,
            borderColor: 'rgb(0 45 109)',
            pointBackgroundColor: 'rgb(0 45 109)',
            pointBorderWidth: 0,
            fill: false,
            backgroundColor: this.isLine ? null : 'rgb(0 45 109)',
            minBarLength: 0,
            tension: 0.4,
            xAxisID: 'x',
            spanGaps: true,
          },
          {
            label: this.periodLabels.comparison,
            data: this.comparisonMap.values,
            borderColor: '#3fb4e5',
            pointBackgroundColor: '#3fb4e5',
            backgroundColor: this.isLine ? null : '#3fb4e5',
            minBarLength: 0,
            borderDash: [10, 5],
            pointBorderWidth: 0,
            fill: false,
            tension: 0.4,
            xAxisID: 'x',
            spanGaps: true,
          },
        ],
      };
      return data;
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        onHover: (e, elements, chart) => {
          const nearest = chart.getElementsAtEventForMode(e, 'nearest', {intersect: true}, false);
          if (nearest && nearest.length) {
            chart.$hoveredDatasetIndex = nearest[0].datasetIndex;
          }
        },
        interaction: {
          mode: 'index',
          intersect: true,
        },
        plugins: {
          legend: {
            position: 'bottom',
            align: 'start',
            labels: {
              usePointStyle: true,
              boxHeight: 8,
              color: '#172344',
              font: {size: 13},
              filter: (item) => item.datasetIndex < 2,
            },
          },
          tooltip: {
            position: 'nearest',
            itemSort: (a, b) => a.datasetIndex - b.datasetIndex,
            filter: (item) => item?.datasetIndex < 2,
            usePointStyle: true,
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems?.length) return '';
                const chart = tooltipItems[0].chart;
                const hoveredIndex = chart.$hoveredDatasetIndex ?? tooltipItems[0].datasetIndex;
                const item = tooltipItems.find((i) => i.datasetIndex === hoveredIndex) || tooltipItems[0];
                if (item.datasetIndex === 1) {
                  return this.comparisonMap.keys[item.dataIndex];
                }
                return this.mainMap.keys[item.dataIndex];
              },
              label: (context) => {
                let label =
                  this.unit === ' €' ? this.formatNumberRM2(context.parsed.y) : this.formatNumberRM(context.parsed.y);
                if (this.unit) label += this.unit;
                return label;
              },
              labelColor: (context) => {
                return {
                  borderColor: context.dataset.borderColor,
                  backgroundColor: context.dataset.borderColor,
                  borderWidth: 0,
                  borderRadius: 2,
                };
              },
            },
            backgroundColor: '#FFF',
            titleColor: '#293656',
            bodyColor: '#172344',
            displayColors: true,
            borderColor: '#e5e7eb',
            borderWidth: 1,
            boxHeight: 8,
            boxPadding: 4,
          },
        },
        scales: {
          x: {
            stacked: this.isLine,
            ticks: {font: {size: 9}},
          },
          y: {
            stacked: false,
            ticks: {font: {size: 9}},
            min: 0,
          },
        },
      };
    },
    improved() {
      return this.delta > 0;
    },
    showToggle() {
      return import.meta.env.VUE_APP_RM_STATS_GRAPH === 'true';
    },
    isLine() {
      return this.graphType === 'line';
    },
    isCurrencyUnit() {
      return this.unit === ' €';
    },
    labelClass() {
      return !this.hasIcon ? '!font-medium' : '';
    },
  },
  methods: {
    openChartDialog() {
      this.$refs.dialog.show();
    },
    displayChartDialog(val) {
      console.log(`displayChartDialog ${val}`);
      if (val === 'graph')
        this.$nextTick(() => {
          this.openChartDialog();
        });
    },
    displayLabel(date) {
      if (this.interval === 'monthly') {
        if (!date) return '-';
        return this.capitalize(moment(date).format('MMM'));
      }
      if (this.interval === 'yearly') {
        if (!date) return '-';
        return moment(date).format('YYYY');
      }
      return this.displayDate(date, '-');
    },
  },
  watch: {
    displayMode(newVal) {
      this.displayModeValue = newVal;
    },
  },
};
</script>

<template>
  <div
    v-bind="$attrs"
    class="border grow border-grey-100 rounded-xl min-w-[295px] h-fit"
    :class="{'rounded-l-none border-l-0': hasIcon}">
    <div
      class="p-4 flex flex-col h-full"
      :class="[{'border-l-[6px] pl-6': hasIcon}, hasIcon ? 'gap-[36px]' : 'gap-4 pb-6']"
      :style="`border-color: ${iconColor}`">
      <div class="flex gap-2 w-full">
        <div
          v-if="hasIcon && !isGraph"
          class="rounded-lg flex justify-center items-center h-[68px] min-w-[68px]"
          :style="`background-color: ${iconBgColor}`">
          <i class="ga-icon text-[32px]" :class="icon" :style="`color: ${iconColor}`" />
        </div>
        <div class="flex flex-col w-full" :class="{'gap-4': !hasIcon || isGraph}">
          <div class="flex gap-3 items-center w-full">
            <slot name="picto" />
            <AkLabel :label-class="labelClass" class="text-grey-900 !mb-0" :information="information">
              {{ label }}
            </AkLabel>
            <button
              v-if="isGraph && !hasError"
              @click="openChartDialog"
              class="ml-auto w-8 aspect-square flex items-center justify-center border rounded-md border-grey-100 hover:bg-grey-50 hover:border-grey-200">
              <i class="ga-icon ga-arrow-expand text-md text-grey-900" />
            </button>
            <AkSelect
              @change="displayChartDialog"
              v-if="showToggle"
              :options="displayOptions"
              v-model="displayModeValue"
              xs
              :class="{'ml-auto': !isGraph || hasError}" />
          </div>
          <template v-if="hasError">
            <div class="w-full flex items-center justify-center flex-col gap-3 p-5 h-full">
              <i
                class="ga-icon ga-warning rounded-full bg-red-50 h-[68px] w-[68px] flex items-center justify-center text-[32px] text-red" />
              <span class="text-red text-center">Une erreur est survenue lors du chargement de cette donnée</span>
            </div>
          </template>
          <template v-else>
            <div class="flex gap-1 items-center">
              <div v-if="isGraph" class="w-full h-[190px]">
                <Chart
                  v-if="!isLoading"
                  :type="graphType"
                  :data="chartData"
                  :options="chartOptions"
                  :height="200"
                  class="w-full" />
                <Vue3Lottie
                  v-else
                  :animationData="require('@assets/animations/line_chart_loading.json')"
                  :loop="true"
                  width="100%" />
              </div>
              <template v-else>
                <AkShimmer :loading="isLoading" width="80px" height="42px">
                  <span class="text-[28px] text-grey-900 font-bold">
                    {{ isCurrencyUnit ? formatNumberRM2(indicator?.main) : formatNumberRM(indicator?.main) }}
                  </span>
                </AkShimmer>
                <span v-if="unit" class="text-md text-grey-500">
                  {{ unit }}
                </span>
                <slot name="suffix" :indicator="indicator?.main" />
              </template>
            </div>
          </template>
        </div>
      </div>
      <div v-if="!isGraph" class="border-t border-grey-100 pt-4">
        <span class="flex items-center gap-1 text-sm text-grey-500">
          Période comparée
          <AkShimmer :loading="isLoading" width="90px" height="18px">
            <span class="text-grey-900">
              {{ isCurrencyUnit ? formatNumberRM2(indicator?.comparison) : formatNumberRM(indicator?.comparison)
              }}{{ unit }}
            </span>
            <template v-if="delta">
              <i v-if="improved" class="ga-icon ga-trend_up text-md text-green"></i>
              <i v-else-if="delta != 0" class="ga-icon ga-trend_down text-md text-red"></i>
              <span v-if="delta != 0" class="font-semibold" :class="improved ? 'text-green' : 'text-red'">
                {{ formatNumber(delta) }}%
              </span>
            </template>
          </AkShimmer>
        </span>
      </div>
    </div>
  </div>

  <IndicatorCardGraphDialog
    ref="dialog"
    :indicator="indicator"
    :label="label"
    :interval="interval"
    :unit="unit"
    :information="information"
    :legendLabels="periodLabels"
    :graph-type="graphType"
    :hideZeroLabels="hideZeroLabels" />
</template>

<style scoped></style>
